import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfilePhotoCard } from './ProfilePhotoCard';
import { EmailSettingsCard } from './EmailSettingsCard';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Icons
import { Settings, User, Lock, Bell, Building, Palette, Save } from 'lucide-react';
import { TenantLogoUploader } from './TenantLogoUploader';
import { currentAcademicYear } from '@/lib/academicYear';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = ({ user, setUser }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  
  // Password form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_notifications: true,
    invoice_notifications: true,
    payment_notifications: true,
    grade_notifications: true,
    absence_notifications: true,
    message_notifications: true,
    report_card_notifications: true
  });
  
  // Tenant settings (for admin)
  const [tenantSettings, setTenantSettings] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    academic_year: currentAcademicYear(),
    grading_system: 'SCALE_20'
  });
  
  // Theme preference
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
    }
    
    fetchNotificationPreferences();
    
    if (user?.role === 'ADMIN' || user?.role === 'PRINCIPAL') {
      fetchTenantSettings();
    }
    
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    applyTheme(savedTheme);
  }, [user]);

  const fetchNotificationPreferences = async () => {
    try {
      const response = await axios.get(`${API}/users/notification-preferences`);
      setNotificationPrefs(response.data);
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const fetchTenantSettings = async () => {
    try {
      const response = await axios.get(`${API}/tenants/settings`);
      setTenantSettings({
        name: response.data.name || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        academic_year: response.data.academic_year || currentAcademicYear(),
        grading_system: response.data.grading_system || 'SCALE_20',
        logo_url: response.data.logo_url || null
      });
    } catch (error) {
      console.error('Error fetching tenant settings:', error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (profileForm.first_name) params.append('first_name', profileForm.first_name);
      if (profileForm.last_name) params.append('last_name', profileForm.last_name);
      if (profileForm.email) params.append('email', profileForm.email);
      if (profileForm.phone) params.append('phone', profileForm.phone);
      
      const response = await axios.put(`${API}/users/profile?${params.toString()}`);
      
      // Update user in parent component
      if (setUser) {
        setUser(response.data);
      }
      
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du profil');
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Mot de passe modifié avec succès');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      // Gérer les erreurs de validation Pydantic (422)
      let errorMessage = 'Erreur lors du changement de mot de passe';
      
      if (error.response?.status === 422) {
        errorMessage = 'Format de mot de passe invalide. Vérifiez vos saisies.';
      } else if (error.response?.data?.detail) {
        // Si detail est un objet (erreur de validation), extraire le message
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg).join(', ');
        }
      }
      
      toast.error(errorMessage);
      console.error('Error changing password:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNotifications = async () => {
    setLoading(true);
    
    try {
      await axios.put(`${API}/users/notification-preferences`, notificationPrefs);
      toast.success('Préférences de notification mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des préférences');
      console.error('Error updating notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTenantSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (tenantSettings.name) params.append('name', tenantSettings.name);
      if (tenantSettings.address) params.append('address', tenantSettings.address);
      if (tenantSettings.phone) params.append('phone', tenantSettings.phone);
      if (tenantSettings.email) params.append('email', tenantSettings.email);
      if (tenantSettings.academic_year) params.append('academic_year', tenantSettings.academic_year);
      if (tenantSettings.grading_system) params.append('grading_system', tenantSettings.grading_system);
      
      await axios.put(`${API}/tenants/settings?${params.toString()}`);
      
      toast.success('Paramètres de l\'établissement mis à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
      console.error('Error updating tenant settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (selectedTheme) => {
    if (selectedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', selectedTheme);
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
    toast.success(`Thème ${newTheme === 'dark' ? 'sombre' : 'clair'} activé`);
  };

  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="p-6 space-y-6 fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center">
          <Settings className="w-6 h-6 mr-2 text-blue-600" />
          Paramètres
        </h1>
        <p className="text-gray-600 mt-1">
          Gérez vos préférences et informations personnelles
        </p>
      </div>

      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full" style={{ gridTemplateColumns: isAdmin ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)' }}>
          <TabsTrigger value="profile">
            <User className="w-4 h-4 mr-2" />
            Profil
          </TabsTrigger>
          <TabsTrigger value="security">
            <Lock className="w-4 h-4 mr-2" />
            Sécurité
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="appearance">
            <Palette className="w-4 h-4 mr-2" />
            Apparence
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="establishment">
              <Building className="w-4 h-4 mr-2" />
              Établissement
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-4">
          <ProfilePhotoCard user={user} setUser={setUser} />
          {['ADMIN', 'PRINCIPAL'].includes(user?.role) && <EmailSettingsCard />}
          <Card>
            <CardHeader>
              <CardTitle>Informations personnelles</CardTitle>
              <CardDescription>
                Mettez à jour vos informations de profil
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prénom</Label>
                    <Input
                      id="first_name"
                      value={profileForm.first_name}
                      onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                      placeholder="Votre prénom"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nom</Label>
                    <Input
                      id="last_name"
                      value={profileForm.last_name}
                      onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                      placeholder="Votre nom"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    placeholder="votre.email@exemple.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    placeholder="+224 6XX XX XX XX"
                  />
                </div>
                
                <div className="flex justify-end">
                  <Button type="submit" disabled={loading} className="btn-primary">
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Changer le mot de passe</CardTitle>
              <CardDescription>
                Assurez-vous d'utiliser un mot de passe fort
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Mot de passe actuel</Label>
                  <PasswordInput
                    id="current_password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new_password">Nouveau mot de passe</Label>
                  <PasswordInput
                    id="new_password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                  <p className="text-xs text-gray-500">Minimum 6 caractères</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
                  <PasswordInput
                    id="confirm_password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                </div>
                
                <div className="flex justify-end">
                  <Button type="submit" disabled={loading} className="btn-primary">
                    <Lock className="w-4 h-4 mr-2" />
                    {loading ? 'Changement...' : 'Changer le mot de passe'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Préférences de notification</CardTitle>
              <CardDescription>
                Choisissez les notifications que vous souhaitez recevoir
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="email_notifications">Notifications par email</Label>
                  <p className="text-sm text-gray-500">Recevoir des emails de notification</p>
                </div>
                <Switch
                  id="email_notifications"
                  checked={notificationPrefs.email_notifications}
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, email_notifications: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="invoice_notifications">Factures</Label>
                  <p className="text-sm text-gray-500">Nouvelles factures et rappels</p>
                </div>
                <Switch
                  id="invoice_notifications"
                  checked={notificationPrefs.invoice_notifications}
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, invoice_notifications: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="grade_notifications">Notes</Label>
                  <p className="text-sm text-gray-500">Nouvelles notes disponibles</p>
                </div>
                <Switch
                  id="grade_notifications"
                  checked={notificationPrefs.grade_notifications}
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, grade_notifications: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="absence_notifications">Absences</Label>
                  <p className="text-sm text-gray-500">Alertes d'absence</p>
                </div>
                <Switch
                  id="absence_notifications"
                  checked={notificationPrefs.absence_notifications}
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, absence_notifications: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="report_card_notifications">Bulletins</Label>
                  <p className="text-sm text-gray-500">Nouveaux bulletins scolaires</p>
                </div>
                <Switch
                  id="report_card_notifications"
                  checked={notificationPrefs.report_card_notifications}
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, report_card_notifications: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="message_notifications">Messages</Label>
                  <p className="text-sm text-gray-500">Nouveaux messages</p>
                </div>
                <Switch
                  id="message_notifications"
                  checked={notificationPrefs.message_notifications}
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, message_notifications: checked })}
                />
              </div>
              
              <div className="flex justify-end pt-4">
                <Button onClick={handleUpdateNotifications} disabled={loading} className="btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Enregistrement...' : 'Enregistrer les préférences'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Thème de l'interface</CardTitle>
              <CardDescription>
                Personnalisez l'apparence de l'application
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Sélectionnez un thème</Label>
                <Select value={theme} onValueChange={handleThemeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-300 mr-2" />
                        Clair
                      </div>
                    </SelectItem>
                    <SelectItem value="dark">
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-gray-900 border-2 border-gray-600 mr-2" />
                        Sombre
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600">
                  Le thème sera appliqué à toute l'application et sauvegardé dans votre navigateur.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Establishment Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="establishment" className="space-y-4">
            <TenantLogoUploader key={tenantSettings.logo_url || 'nologo'} initialUrl={tenantSettings.logo_url} />
            <Card>
              <CardHeader>
                <CardTitle>Paramètres de l'établissement</CardTitle>
                <CardDescription>
                  Gérez les informations de votre établissement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateTenantSettings} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tenant_name">Nom de l'établissement</Label>
                    <Input
                      id="tenant_name"
                      value={tenantSettings.name}
                      onChange={(e) => setTenantSettings({ ...tenantSettings, name: e.target.value })}
                      placeholder="École Primaire..."
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="tenant_address">Adresse</Label>
                    <Input
                      id="tenant_address"
                      value={tenantSettings.address}
                      onChange={(e) => setTenantSettings({ ...tenantSettings, address: e.target.value })}
                      placeholder="Adresse complète"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tenant_phone">Téléphone</Label>
                      <Input
                        id="tenant_phone"
                        type="tel"
                        value={tenantSettings.phone}
                        onChange={(e) => setTenantSettings({ ...tenantSettings, phone: e.target.value })}
                        placeholder="+224 6XX XX XX XX"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="tenant_email">Email</Label>
                      <Input
                        id="tenant_email"
                        type="email"
                        value={tenantSettings.email}
                        onChange={(e) => setTenantSettings({ ...tenantSettings, email: e.target.value })}
                        placeholder="contact@ecole.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="academic_year">Année Scolaire</Label>
                      <Input
                        id="academic_year"
                        value={tenantSettings.academic_year}
                        onChange={(e) => setTenantSettings({ ...tenantSettings, academic_year: e.target.value })}
                        placeholder="2024-2025"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Système de notation par niveau</Label>
                      <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-500"></div>
                          <div>
                            <p className="font-medium text-sm">Maternelle et Primaire</p>
                            <p className="text-sm text-gray-600">Notation sur <span className="font-semibold">10</span></p>
                            <p className="text-xs text-gray-500 mt-1">Maternelle, PS, GS, CP1, CP2, CE1, CE2, CM1, CM2</p>
                          </div>
                        </div>
                        
                        <div className="border-t pt-3"></div>
                        
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-green-500"></div>
                          <div>
                            <p className="font-medium text-sm">Collège et Lycée</p>
                            <p className="text-sm text-gray-600">Notation sur <span className="font-semibold">20</span></p>
                            <p className="text-xs text-gray-500 mt-1">7ᵉ année à Terminale</p>
                          </div>
                        </div>
                        
                        <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                          <p className="text-xs text-blue-800">
                            <span className="font-semibold">📝 Note :</span> Le système de notation s'applique automatiquement selon le niveau de la classe. Cette configuration suit le système éducatif guinéen.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <Button type="submit" disabled={loading} className="btn-primary">
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
